import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventContentArg, FormatterInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import type { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
    endOfDay,
    endOfMonth,
    format,
    startOfDay,
    startOfMonth,
    subDays,
} from 'date-fns';

import { Conditional } from '@klp/conditional';
import { useAppParams } from '@klp/core/app';
import { Block } from '@klp/design/Block';
import { ErrorNotification } from '@klp/errorNotification';

import { convertByggMoteromCalendarEntry } from '../../classes/ByggMoteromCalendarEntry';
import { dateToIsoDateTimeString } from '../../classes/ISODateTime';
import {
    useLazyGetByggMoteromCalendarQuery,
    useGetByggMoteromQuery,
} from '../../rtkApi';

// Use simple locale codes to avoid import issues
const calendarLocaleMap: Record<string, string> = {
    no: 'nb',
    da: 'da',
    sv: 'sv',
    en: 'en-gb',
};

const useStyles = makeStyles((theme: Theme) => ({
    calendar: {
        '& .fc .fc-meetingRoom-button': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.background.default,
            border: 0,
            pointerEvents: 'none',
            fontSize: theme.typography.h3.fontSize,
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            padding: 0,
        },
    },
    calendarEntry: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1,
    },
}));

type ByggMoteromCalendarProps = {
    byggNummer: string;
};

const timeFormat: FormatterInput = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
};

export const ByggMoteromCalendar = ({
    byggNummer,
}: ByggMoteromCalendarProps) => {
    const classes = useStyles();
    const { selectedClient } = useAppParams();
    const kundenummer = selectedClient?.kundeNummer;
    const { i18n } = useTranslation();
    const locale = i18n.language;

    // Initialize with current month's date range
    const [dateRange, setDateRange] = useState<
        { from: string; to: string } | undefined
    >(() => {
        const now = new Date();
        return {
            from: dateToIsoDateTimeString(startOfDay(startOfMonth(now))),
            to: dateToIsoDateTimeString(endOfDay(endOfMonth(now))),
        };
    });

    // RTK Query - fetch meeting rooms list
    const { data: byggMoteromList, error: byggMoteromListError } =
        useGetByggMoteromQuery(
            { byggNummer, kundenummer },
            { skip: !byggNummer || !kundenummer },
        );

    // RTK Query - lazy fetch calendar entries (only when we have all params)
    const [fetchCalendar, { data: byggMoteromCalendar, isFetching }] =
        useLazyGetByggMoteromCalendarQuery();

    // Fetch calendar when we have all required parameters
    useEffect(() => {
        if (byggNummer && kundenummer && dateRange?.from && dateRange?.to) {
            fetchCalendar({
                byggNummer,
                kundenummer,
                from: dateRange.from,
                to: dateRange.to,
            });
        }
    }, [
        byggNummer,
        kundenummer,
        dateRange?.from,
        dateRange?.to,
        fetchCalendar,
    ]);

    // Convert calendar entries - include dateRange in dependencies to reset when date changes
    const items = useMemo(() => {
        // Don't show stale data while fetching new data for different date range
        if (isFetching) {
            return [];
        }
        return (byggMoteromCalendar ?? []).map(convertByggMoteromCalendarEntry);
    }, [byggMoteromCalendar, isFetching, dateRange]);

    const handleDateChange = ({ start, end }: { start: Date; end: Date }) => {
        const newFrom = dateToIsoDateTimeString(startOfDay(start));
        const newTo = dateToIsoDateTimeString(endOfDay(subDays(end, 1)));

        if (dateRange?.from === newFrom && dateRange?.to === newTo) {
            return;
        }

        setDateRange({ from: newFrom, to: newTo });
    };

    const renderEventContent = (eventInfo: EventContentArg) => {
        const { start, end, title } = eventInfo.event;

        return (
            <div title={title} className={classes.calendarEntry}>
                <b>
                    {start ? format(start, 'HH:mm') : ''} –{' '}
                    {end ? format(end, 'HH:mm') : ''}
                </b>{' '}
                <span>{`${title} (${eventInfo.event.extendedProps.name})`}</span>
            </div>
        );
    };

    const roomNames = byggMoteromList?.map(({ name }) => name).join(', ') ?? '';

    return (
        <Block marginTop="fat" className={classes.calendar}>
            <Conditional
                condition={!byggMoteromListError}
                errorComponent={
                    <ErrorNotification
                        title="byggCalendar.error.title"
                        description="byggCalendar.error.description"
                    />
                }
            >
                <FullCalendar
                    plugins={[dayGridPlugin]}
                    initialView="dayGridMonth"
                    weekends={true}
                    firstDay={1}
                    weekNumbers={true}
                    events={items}
                    eventContent={renderEventContent}
                    eventTimeFormat={timeFormat}
                    datesSet={handleDateChange}
                    locale={calendarLocaleMap[locale]}
                    customButtons={{
                        meetingRoom: {
                            text: roomNames || '',
                        },
                    }}
                    headerToolbar={{
                        start: 'title',
                        center: 'meetingRoom',
                        end: 'today prev,next',
                    }}
                />
            </Conditional>
        </Block>
    );
};
